import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Lightning, ArrowRight, X as Close } from "@phosphor-icons/react";
import { Button } from "./ui/button";

/**
 * OnboardingTour
 *
 * Lightweight 4-step guided tour anchored to real DOM elements via their
 * `data-testid` attributes. The tour:
 *   - Renders a darkening backdrop with a spotlight cutout around the target
 *   - Renders a popover positioned below (or above) the target
 *   - Tracks completion via localStorage so it only fires once per browser
 *
 * Mounted by the Dashboard. Auto-starts the first time a user lands there.
 *
 * Props:
 *   storageKey:   localStorage key (default: "fg_onboarding_v1_complete")
 *   onComplete:   optional callback when the user finishes or skips the tour
 *   forceShow:    if true, ignore localStorage and always show (for "Take a quick tour" button)
 */

const STORAGE_KEY_DEFAULT = "fg_onboarding_v1_complete";

const STEPS = [
  {
    selector: '[data-testid="dashboard-page"]',
    placement: "center",
    title: "Welcome to FlashGrants",
    body:
      "We'll walk you through the four things every nonprofit uses most. About 30 seconds — skip anytime.",
  },
  {
    selector: '[data-testid="nav-grants"]',
    placement: "bottom",
    title: "Browse verified SC grants",
    body:
      "Real foundation, federal, and corporate grants — sorted by your Best-Match Score. Bookmark anything that fits to come back to later.",
  },
  {
    selector: '[data-testid="nav-scout"]',
    placement: "bottom",
    title: "Corporate Scout (Premium)",
    body:
      "Verified SC corporate funders with documented CSR programs. Filter by who matches employee gifts, find their LinkedIn contacts, and one-click a 3-tier sponsorship proposal.",
  },
  {
    selector: '[data-testid="nav-profile"]',
    placement: "bottom",
    title: "Complete your profile first",
    body:
      "Mission, focus areas, county, and budget — this powers your Best-Match Score, Flash Drafts, and Corporate Scout matches. Takes 3 minutes.",
  },
];

const padding = 8; // px around the spotlight target
const popoverGap = 14; // px between target and popover
const popoverMaxWidth = 360;

const getTargetRect = (selector) => {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return r;
};

const OnboardingTour = ({
  storageKey = STORAGE_KEY_DEFAULT,
  onComplete,
  forceShow = false,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [active, setActive] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  // Decide whether to start the tour
  useEffect(() => {
    if (forceShow) {
      setActive(true);
      setStepIndex(0);
      return;
    }
    try {
      const done = localStorage.getItem(storageKey);
      if (!done) {
        // Small delay so the dashboard has mounted and laid out
        const t = setTimeout(() => setActive(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage disabled — just skip the tour */
    }
  }, [forceShow, storageKey]);

  const finish = useCallback(
    (completed) => {
      setActive(false);
      try {
        localStorage.setItem(storageKey, completed ? "1" : "skipped");
      } catch {
        /* ignore */
      }
      if (onComplete) onComplete(completed);
    },
    [onComplete, storageKey]
  );

  const step = STEPS[stepIndex];

  // Recompute target rect on step change + on resize/scroll
  useLayoutEffect(() => {
    if (!active || !step) return;
    if (step.placement === "center") {
      setTargetRect(null);
      return;
    }
    let frame = null;
    const recalc = () => {
      // Scroll target into view if it's off-screen
      const el = document.querySelector(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) {
          el.scrollIntoView({ behavior: "instant", block: "center" });
        }
      }
      frame = requestAnimationFrame(() => {
        setTargetRect(getTargetRect(step.selector));
      });
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [active, step]);

  if (!active || !step) return null;

  const isLast = stepIndex === STEPS.length - 1;
  const isCenter = step.placement === "center" || !targetRect;

  // Build popover position
  let popoverStyle = {};
  if (isCenter) {
    popoverStyle = {
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    };
  } else {
    const placement = step.placement === "top" ? "top" : "bottom";
    const left = Math.max(
      16,
      Math.min(
        window.innerWidth - popoverMaxWidth - 16,
        targetRect.left + targetRect.width / 2 - popoverMaxWidth / 2
      )
    );
    if (placement === "bottom") {
      popoverStyle = { top: targetRect.bottom + popoverGap, left };
    } else {
      popoverStyle = {
        bottom: window.innerHeight - targetRect.top + popoverGap,
        left,
      };
    }
  }

  const spotlight = targetRect ? (
    <div
      className="onboarding-spotlight"
      style={{
        position: "fixed",
        top: targetRect.top - padding,
        left: targetRect.left - padding,
        width: targetRect.width + padding * 2,
        height: targetRect.height + padding * 2,
        borderRadius: 8,
        boxShadow:
          "0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 2px rgba(16,185,129,0.7), 0 0 28px rgba(16,185,129,0.45)",
        pointerEvents: "none",
        transition: "all 220ms ease",
        zIndex: 9998,
      }}
      data-testid="onboarding-spotlight"
    />
  ) : (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.78)",
        pointerEvents: "none",
        zIndex: 9998,
      }}
    />
  );

  const popover = (
    <div
      role="dialog"
      aria-label={step.title}
      data-testid={`onboarding-step-${stepIndex + 1}`}
      style={{
        position: "fixed",
        zIndex: 9999,
        maxWidth: popoverMaxWidth,
        width: "calc(100% - 32px)",
        ...popoverStyle,
      }}
      className="bg-zinc-950 border border-emerald-500/40 rounded-md shadow-2xl p-5 animate-fade-in"
    >
      <button
        type="button"
        onClick={() => finish(false)}
        className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1"
        aria-label="Skip the tour"
        data-testid="onboarding-skip-btn"
      >
        <Close className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Lightning weight="fill" className="w-4 h-4 text-emerald-400" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">
          Step {stepIndex + 1} of {STEPS.length}
        </span>
      </div>
      <h3 className="text-lg font-bold text-white mb-1.5">{step.title}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed">{step.body}</p>
      <div className="flex items-center justify-between mt-5">
        <button
          type="button"
          onClick={() => finish(false)}
          className="text-xs text-zinc-500 hover:text-zinc-300"
          data-testid="onboarding-skip-link"
        >
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {stepIndex > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              className="border-zinc-700 rounded-sm"
              data-testid="onboarding-back-btn"
            >
              Back
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              if (isLast) finish(true);
              else setStepIndex((i) => i + 1);
            }}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="onboarding-next-btn"
          >
            {isLast ? "Got it" : "Next"}
            {!isLast && <ArrowRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {spotlight}
      {popover}
    </>,
    document.body
  );
};

export default OnboardingTour;
